import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { DxDataGridModule, DxTagBoxModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxiButtonModule, DxiColumnModule, DxiValidationRuleModule, DxoColumnFixingModule, DxoEditingModule, DxoPopupModule } from 'devextreme-angular/ui/nested';
import { InitNewRowEvent, RowInsertingEvent, RowRemovingEvent, RowUpdatingEvent } from 'devextreme/ui/data_grid';
import { ValueChangedEvent } from 'devextreme/ui/tag_box';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { IdentityService } from '../services/identity.service';
import { GridService } from 'src/app/shared/grid/grid.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { IdentityApplication } from '../models/identity-application';
import { IdentityScope } from '../models/identity-scope';

@Component({
  templateUrl: './identity-clients.component.html',
  styleUrls: ['./identity-clients.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoPopupModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule, DxTemplateModule, DxTagBoxModule]
})
export class IdentityClientsComponent implements OnInit {
  private readonly identityService = inject(IdentityService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly toastService = inject(ToastService);
  private readonly gridService = inject(GridService);

  private readonly blanksLabel = '(Blanks)';

  readonly allowUpdating = signal(false);
  readonly allowAdding = signal(false);
  readonly allowDeleting = signal(false);
  readonly allowShowLog = signal(false);

  readonly scopes = signal<IdentityScope[]>([]);
  readonly clients = signal<IdentityApplication[]>([]);

  ngOnInit(): void {
    this.allowAdding.set(this.currentUserService.hasPermission('Identity_Action_Client_Create'));
    this.allowUpdating.set(this.currentUserService.hasPermission('Identity_Action_Client_Edit'));
    this.allowDeleting.set(this.currentUserService.hasPermission('Identity_Action_Client_Delete'));
    this.allowShowLog.set(this.currentUserService.hasPermission('Identity_Action_Client_ShowLog'));
    void this.refresh();
  }

  readonly refresh = async (): Promise<void> => {
    this.scopes.set(await this.identityService.getScopes());
    this.clients.set(await this.identityService.getApplications());
  };

  // Used by the Scopes column to sort rows by the resolved scope display names.
  readonly calculateScopesSortValue = (rowData: IdentityApplication): string =>
    (rowData.Scopes ?? [])
      .map(scope => this.scopes().find(item => item.Name === scope)?.DisplayName ?? scope)
      .sort((a, b) => a.localeCompare(b))
      .join(' ');

  // Used by the Scopes column to support text filtering and the '(Blanks)' option.
  readonly calculateScopesArrayFilterExpression = (filterValue: string): ((rowData: IdentityApplication) => boolean) =>
    rowData => {
      const clientScopes = rowData.Scopes ?? [];
      const normalizedFilterValue = filterValue.toLowerCase();
      const res = filterValue === this.blanksLabel
        ? clientScopes.length === 0
        : clientScopes.some(scope => scope.toLowerCase().includes(normalizedFilterValue));
      return res;
    };

  // Used by the Scopes header filter datasource to provide unique scope options with display text and raw values.
  readonly getScopesHeaderFilterData = (): Array<{ text: string; value: string; }> =>
    this.clients()
      .flatMap(client => client.Scopes?.length ? client.Scopes : [this.blanksLabel])
      .filter((scope, idx, self) => self.indexOf(scope) === idx)
      .map(scope => ({
        text: this.scopes().find(item => item.Name === scope)?.DisplayName ?? scope,
        value: scope
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

  readonly onValueChanged = (ev: ValueChangedEvent, cell: { setValue: (value: IdentityApplication['Scopes']) => void; }): void => {
    const value = Array.isArray(ev.value) ? ev.value.filter((item): item is string => typeof item === 'string') : [];
    cell.setValue(value);
  };

  readonly onInitNewRow = (ev: InitNewRowEvent<IdentityApplication, string>): void => {
    ev.data = {
      Id: '', ClientId: '', HasClientSecret: false, ClientSecret: '', ConcurrencyToken: '', ConsentType: '', DisplayName: '', DisplayNames: [], Endpoints: ['authorization', 'token'],
      GrantTypes: ['password', 'refresh_token', 'authorization_code'], ResponseTypes: ['code'], Scopes: ['api'], PostLogoutRedirectUri: '', PostLogoutRedirectUris: [], RedirectUri: '',
      RedirectUris: [], Requirements: [], Type: ''
    };
  };

  readonly onRowInserting = (ev: RowInsertingEvent<IdentityApplication, string>): void => {
    ev.cancel = this.gridService.handleDataGridRow(ev.data, (item) => this.createApplication(item));
  };

  readonly onRowUpdating = (ev: RowUpdatingEvent<IdentityApplication, string>): void => {
    const updatedItem: IdentityApplication = { ...ev.oldData, ...ev.newData };
    ev.cancel = this.gridService.handleDataGridRow(updatedItem, (item) => this.updateApplication(item), ev.newData);
  };

  readonly onRowRemoving = (ev: RowRemovingEvent<IdentityApplication, string>): void => {
    ev.cancel = this.gridService.handleDataGridRow(ev.data, (item) => this.deleteApplication(item));
  };

  private async createApplication(item: IdentityApplication): Promise<IdentityApplication> {
    const newItem = await this.identityService.createApplication(item, false);
    this.toastService.showToast('Identity application created...', ToastType.success);
    return newItem;
  }

  private async updateApplication(item: IdentityApplication): Promise<IdentityApplication> {
    const newItem = await this.identityService.updateApplication(item, false);
    this.toastService.showToast('Identity application updated...', ToastType.success);
    return newItem;
  }

  private async deleteApplication(item: IdentityApplication): Promise<IdentityApplication> {
    await this.identityService.deleteApplication(item.Id, false);
    this.toastService.showToast('Identity application deleted...', ToastType.success);
    return item;
  }
}
